// screens/professional/UploadResumeScreen.js
// JobEng — Resume & Cover Letter management.
// Full rebuild: multi-resume library, cover letter library with 4 templates,
// job tailoring, inline preview, ATS score breakdown, improvement suggestions,
// share via native sheet, export to PDF/TXT, version history.
//
// Backend endpoints expected (all gated by API key + bearer token):
//   POST    /api/users/:id/documents/upload         (multipart: file, document_type, document_name)
//   GET     /api/users/:id/documents                (?document_type=resume|cover_letter)
//   DELETE  /api/users/:id/documents/:doc_id
//   POST    /api/users/:id/documents/:doc_id/set-active

/* eslint-disable no-unused-vars */
let _profUserId = 'default';
try {
  require('@react-native-async-storage/async-storage').default.getItem('userToken').then(function(t){if(t)_profUserId=t.slice(0,16);}).catch(function(){});
} catch (e) {}

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Image,
  ActivityIndicator,
  Alert,
  Platform,
  Modal,
  TextInput,
  Share,
  KeyboardAvoidingView,
  FlatList,
  Animated,
  Easing,
  RefreshControl,
  Pressable,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons, FontAwesome5 } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';
import * as FileSystem from 'expo-file-system/legacy';
import AsyncStorage from '@react-native-async-storage/async-storage';

import ProfileManagementService from '../../services/ProfileManagementService';
import PROF_KEYS from '../../constants/profileKeys';

// ──────────────────────────────────────────────────────────────────────────────
// Config & constants
// ──────────────────────────────────────────────────────────────────────────────
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const ACCEPTED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'image/png',
  'image/jpeg',
];

const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB

const COLORS = {
  primary: '#0e4e96',
  primaryDark: '#0a3d75',
  accent: '#8B5CF6',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  text: '#0F172A',
  sub: '#64748B',
  light: '#94A3B8',
  border: '#E2E8F0',
  bg: '#F8FAFC',
  surface: '#FFFFFF',
};

const COVER_LETTER_TEMPLATES = [
  {
    id: 'professional',
    name: 'Professional',
    icon: 'briefcase',
    color: '#0e4e96',
    description: 'Formal, structured, traditional industries (finance, law, consulting)',
  },
  {
    id: 'modern',
    name: 'Modern',
    icon: 'rocket',
    color: '#8B5CF6',
    description: 'Confident, results-oriented, tech and startup roles',
  },
  {
    id: 'creative',
    name: 'Creative',
    icon: 'color-palette',
    color: '#EC4899',
    description: 'Personable, story-driven, design and creative roles',
  },
  {
    id: 'brief',
    name: 'Brief',
    icon: 'flash',
    color: '#10B981',
    description: 'Short and direct, for casual or fast-moving applications',
  },
];

// ──────────────────────────────────────────────────────────────────────────────
// Utility helpers
// ──────────────────────────────────────────────────────────────────────────────
const formatBytes = (bytes = 0) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${units[i]}`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
};

const fileIconForMime = (mime = '') => {
  if (mime.includes('pdf')) return 'file-pdf-box';
  if (mime.includes('word') || mime.includes('document')) return 'file-word-box';
  if (mime.includes('image')) return 'file-image';
  if (mime.includes('text')) return 'file-document-outline';
  return 'file-outline';
};

const btoaSafe = (str) => {
  if (typeof btoa === 'function') return btoa(unescape(encodeURIComponent(str)));
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
  let output = '';
  const input = unescape(encodeURIComponent(str));
  for (let block = 0, charCode, i = 0, map = chars;
       input.charAt(i | 0) || ((map = '='), i % 1);
       output += map.charAt(63 & (block >> (8 - (i % 1) * 8)))) {
    charCode = input.charCodeAt((i += 3 / 4));
    if (charCode > 0xff) return '';
    block = (block << 8) | charCode;
  }
  return output;
};

// ──────────────────────────────────────────────────────────────────────────────
// Cover letter template engines
// Each takes resumeData + jobContext and returns a multi-paragraph letter.
// ──────────────────────────────────────────────────────────────────────────────
const extractResumeFacts = (resumeData) => {
  const personalInfo = resumeData?.personalInfo || resumeData || {};
  const name =
    personalInfo.name ||
    personalInfo.fullName ||
    resumeData?.fullName ||
    resumeData?.name ||
    'Your Name';
  const summary =
    resumeData?.summary ||
    resumeData?.professionalSummary ||
    personalInfo.summary ||
    '';
  const skillsArr = Array.isArray(resumeData?.skills) ? resumeData.skills : [];
  const skills = skillsArr
    .map(s => (typeof s === 'string' ? s : s?.name || s?.title || s?.label))
    .filter(Boolean);
  const experiences = Array.isArray(resumeData?.experience) ? resumeData.experience : [];
  const educations = Array.isArray(resumeData?.education) ? resumeData.education : [];
  const recent = experiences[0] || {};
  const totalYears = experiences.reduce((sum, e) => {
    const start = e.startYear || (e.startDate && parseInt(e.startDate.slice(0, 4), 10));
    const end = e.endYear || (e.endDate && parseInt(e.endDate.slice(0, 4), 10)) || new Date().getFullYear();
    if (start && end && end >= start) return sum + (end - start);
    return sum;
  }, 0);
  return { name, summary, skills, experiences, educations, recent, totalYears };
};

const renderProfessional = (resume, ctx) => {
  const { name, summary, skills, recent, totalYears } = resume;
  const { jobTitle, company } = ctx;
  const greeting = company ? `Dear ${company} Hiring Team,` : 'Dear Hiring Manager,';
  const opening =
    `I am writing to express my strong interest in the ${jobTitle} position` +
    (company ? ` at ${company}.` : '.') +
    (summary ? ` ${summary}` : '');
  const expPara = recent && recent.title
    ? `In my most recent role as ${recent.title}${recent.company ? ` at ${recent.company}` : ''}, ${
        recent.description ||
        recent.summary ||
        'I contributed to key initiatives, collaborated across teams, and delivered measurable results.'
      }${totalYears > 0 ? ` Over ${totalYears}+ years in this field` : ''}, I have developed a comprehensive skill set that positions me to contribute immediately.`
    : 'My background has equipped me with the skills and experience needed to excel in this role.';
  const skillsPara = skills.length
    ? `My core competencies include ${skills.slice(0, 6).join(', ')}, all of which align directly with the requirements of this position.`
    : 'My broad professional background prepares me well for this opportunity.';
  const closing =
    `I would welcome the opportunity to discuss how my experience can contribute to ${company || 'your team'}. Thank you for considering my application.\n\nSincerely,\n${name}`;
  return `${greeting}\n\n${opening}\n\n${expPara}\n\n${skillsPara}\n\n${closing}`;
};

const renderModern = (resume, ctx) => {
  const { name, skills, recent, totalYears } = resume;
  const { jobTitle, company } = ctx;
  const greeting = company ? `Hi ${company} team,` : 'Hi there,';
  const hook = `When I saw the ${jobTitle} opening${company ? ` at ${company}` : ''}, I knew I had to apply.`;
  const why = recent && recent.title
    ? `At ${recent.company || 'my last role'} I led ${String(recent.title ?? '').toLowerCase()} work that ${
        recent.description || 'shipped real outcomes and made the team faster'
      }.${totalYears > 0 ? ` ${totalYears}+ years in this space has taught me what scales and what doesn't.` : ''}`
    : `My background has been all about shipping outcomes that move the needle.`;
  const skillsPara = skills.length
    ? `What I'd bring: ${skills.slice(0, 5).join(' • ')}.`
    : '';
  const closing =
    `Happy to walk through specifics whenever works for you. Thanks for taking a look.\n\n${name}`;
  return `${greeting}\n\n${hook}\n\n${why}\n\n${skillsPara}\n\n${closing}`;
};

const renderCreative = (resume, ctx) => {
  const { name, summary, skills, recent } = resume;
  const { jobTitle, company } = ctx;
  const greeting = company ? `Hello ${company},` : 'Hello,';
  const story = summary
    ? `${summary} That's the short version. The longer version is why I'm writing.`
    : `Every great role starts with a story, and here's where mine intersects with yours.`;
  const passion = `Your ${jobTitle} opportunity${company ? ` at ${company}` : ''} caught my eye because it sits right at the intersection of what I love doing and what I've spent years getting good at.`;
  const exp = recent && recent.title
    ? `Most recently at ${recent.company || 'my previous company'}, I worked as ${recent.title}, and the highlight wasn't a metric — it was the moment I realised this kind of work energises me.`
    : `Across my career, the moments I'm proudest of are the ones where craft and curiosity overlap.`;
  const skillsPara = skills.length
    ? `Tools and skills I bring along: ${skills.slice(0, 5).join(', ')}.`
    : '';
  const closing = `If any of this resonates, I'd love to hear more about what you're building. Thanks for reading this far.\n\nWarmly,\n${name}`;
  return `${greeting}\n\n${story}\n\n${passion}\n\n${exp}\n\n${skillsPara}\n\n${closing}`;
};

const renderBrief = (resume, ctx) => {
  const { name, skills, recent } = resume;
  const { jobTitle, company } = ctx;
  const greeting = company ? `Hi ${company} team,` : 'Hi,';
  const pitch = `I'd like to apply for the ${jobTitle} role${company ? ` at ${company}` : ''}.${
    recent && recent.title
      ? ` Most recently I was ${recent.title}${recent.company ? ` at ${recent.company}` : ''}.`
      : ''
  }${skills.length ? ` Skills: ${skills.slice(0, 4).join(', ')}.` : ''}`;
  const closing = `My resume is attached. Happy to chat.\n\n${name}`;
  return `${greeting}\n\n${pitch}\n\n${closing}`;
};

const COVER_LETTER_RENDERERS = {
  professional: renderProfessional,
  modern: renderModern,
  creative: renderCreative,
  brief: renderBrief,
};

// ──────────────────────────────────────────────────────────────────────────────
// Job tailoring engine
// Given resume data and a pasted job description, return suggested matches and gaps.
// ──────────────────────────────────────────────────────────────────────────────
const tailorToJob = (resume, jobDescription) => {
  if (!jobDescription || !jobDescription.trim()) return null;
  const text = jobDescription.toLowerCase();
  const skills = (resume.skills || []).map(s => (typeof s === 'string' ? s : s?.name || s?.title)).filter(Boolean);
  const matched = skills.filter(s => text.includes(s.toLowerCase()));

  // Naive keyword extraction: split on punctuation, filter to title-cased or ALL-CAPS tokens > 2 chars
  const tokens = jobDescription
    .split(/[^A-Za-z0-9+/.#\-]+/)
    .filter(t => t.length > 2)
    .filter(t => /^[A-Z]/.test(t) || /^[A-Z]{2,}$/.test(t));
  const uniqueTokens = Array.from(new Set(tokens.map(t => t.trim()))).slice(0, 30);
  const skillsLower = new Set(skills.map(s => s.toLowerCase()));
  const gaps = uniqueTokens.filter(t => !skillsLower.has(t.toLowerCase()) && t.length > 3).slice(0, 8);

  // Power verbs found in JD that resume should mirror
  const powerVerbs = ['lead', 'led', 'built', 'shipped', 'delivered', 'launched', 'scaled', 'designed', 'managed', 'owned', 'drove', 'optimized', 'reduced', 'increased'];
  const verbsInJD = powerVerbs.filter(v => text.includes(v));

  return {
    matched_skills: matched,
    suggested_keywords: gaps,
    power_verbs: verbsInJD,
    match_percentage: skills.length ? Math.round((matched.length / Math.max(skills.length, 1)) * 100) : 0,
  };
};

// ══════════════════════════════════════════════════════════════════════════════
// MAIN SCREEN
// ══════════════════════════════════════════════════════════════════════════════
export default function UploadResumeScreen({ navigation, route }) {
  // ── Identity ────────────────────────────────────────────────────────────────
  const [userId, setUserId] = useState(null);

  // ── Tab state ───────────────────────────────────────────────────────────────
  const [tab, setTab] = useState('resumes'); // 'resumes' | 'letters' | 'tailor'

  // ── Resumes library ─────────────────────────────────────────────────────────
  const [resumes, setResumes] = useState([]);
  const [activeResumeId, setActiveResumeId] = useState(null);
  const [resumeData, setResumeData] = useState(null);
  const [resumeScore, setResumeScore] = useState(0);
  const [aiFeedback, setAiFeedback] = useState([]);

  // ── Cover letters library ───────────────────────────────────────────────────
  const [coverLetters, setCoverLetters] = useState([]);

  // ── Modal visibility ────────────────────────────────────────────────────────
  const [coverLetterVisible, setCoverLetterVisible] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [shareVisible, setShareVisible] = useState(false);
  const [scoreModalVisible, setScoreModalVisible] = useState(false);
  const [tipsVisible, setTipsVisible] = useState(false);
  const [tailorVisible, setTailorVisible] = useState(false);

  // ── Cover letter form state ─────────────────────────────────────────────────
  const [clTemplate, setClTemplate] = useState('professional');
  const [clJobTitle, setClJobTitle] = useState('');
  const [clCompany, setClCompany] = useState('');
  const [clText, setClText] = useState('');
  const [clEditingId, setClEditingId] = useState(null);
  const [generatingCL, setGeneratingCL] = useState(false);
  const [savingCL, setSavingCL] = useState(false);

  // ── Job tailoring state ─────────────────────────────────────────────────────
  const [jobDescription, setJobDescription] = useState('');
  const [tailorResult, setTailorResult] = useState(null);

  // ── Share target ────────────────────────────────────────────────────────────
  const [shareItem, setShareItem] = useState(null); // { type: 'resume'|'letter', data: {...} }

  // ── Upload/analysis state ───────────────────────────────────────────────────
  const [isUploading, setIsUploading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  // ── Animations ──────────────────────────────────────────────────────────────
  const tabIndicator = useRef(new Animated.Value(0)).current;

  // ════════════════════════════════════════════════════════════════════════════
  // Bootstrap
  // ════════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    (async () => {
      const boot = await ProfileManagementService.bootstrap();
      if (boot.success && boot.data?.userId) {
        setUserId(boot.data.userId);
      } else {
        const tok = await AsyncStorage.getItem(PROF_KEYS.USER_TOKEN);
        const uid = (await AsyncStorage.getItem(PROF_KEYS.USER_ID)) || (tok ? tok.slice(0, 16) : null);
        setUserId(uid);
        ProfileManagementService.setUserId(uid);
      }
    })();
  }, []);

  useEffect(() => {
    if (userId) {
      loadResumes();
      loadCoverLetters();
    }
  }, [userId]);

  useEffect(() => {
    const tabIndex = ['resumes', 'letters', 'tailor'].indexOf(tab);
    Animated.timing(tabIndicator, {
      toValue: tabIndex,
      duration: 220,
      easing: Easing.out(Easing.quad),
      useNativeDriver: false,
    }).start();
  }, [tab]);

  // ════════════════════════════════════════════════════════════════════════════
  // Resume operations
  // ════════════════════════════════════════════════════════════════════════════
  const loadResumes = async () => {
    if (!userId) return;
    try {
      const result = await ProfileManagementService.listResumes();
      const list = result.success ? (result.data || []) : [];
      setResumes(list);

      // Determine active resume
      const activeId = await ProfileManagementService.getActiveResumeId();
      const active = list.find(r => String(r.id) === String(activeId)) || list.find(r => r.is_active) || list[0];
      if (active) {
        setActiveResumeId(active.id);
        await loadResumeAnalysis(active);
      }
    } catch (e) {
      console.warn('loadResumes:', e.message);
    }
  };

  const loadResumeAnalysis = async (resume) => {
    try {
      // Try cached analysis first
      const cached = await ProfileManagementService.loadResumeAnalysis(resume.id);
      if (cached) {
        setResumeData(cached.data || null);
        setResumeScore(cached.score || 0);
        setAiFeedback(cached.feedback || []);
        return;
      }
      // Otherwise pull from active profile + compute fresh
      const profileResult = await ProfileManagementService.getProfile();
      const parsed = profileResult.success ? profileResult.data : (resume.parsed_data || null);
      if (parsed) {
        const localScore = ProfileManagementService.calculateLocalATSScore(parsed);
        const feedback = ProfileManagementService.generateLocalFeedback(parsed);
        setResumeData(parsed);
        setResumeScore(localScore.score);
        setAiFeedback(feedback);
        await ProfileManagementService.saveResumeAnalysis(resume.id, {
          data: parsed,
          score: localScore.score,
          feedback,
        });
      }
    } catch (e) {
      console.warn('loadResumeAnalysis:', e.message);
    }
  };

  const pickAndUploadResume = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ACCEPTED_TYPES,
        copyToCacheDirectory: true,
        multiple: false,
      });
      if (result.canceled) return;
      const asset = result.assets?.[0];
      if (!asset) return;

      if (asset.size && asset.size > MAX_FILE_BYTES) {
        Alert.alert('File too large', `Maximum file size is ${formatBytes(MAX_FILE_BYTES)}.`);
        return;
      }

      setIsUploading(true);
      setUploadProgress(0);

      const info = await FileSystem.getInfoAsync(asset.uri);
      if (!info.exists) {
        setIsUploading(false);
        Alert.alert('File not found', 'Could not access the selected file.');
        return;
      }

      setUploadProgress(40);

      const uri = Platform.OS === 'ios' ? asset.uri.replace('file://', '') : asset.uri;
      const file = {
        uri,
        type: asset.mimeType || 'application/octet-stream',
        name: asset.name || `resume-${Date.now()}.pdf`,
      };

      const uploadResult = await ProfileManagementService.uploadResume(file, {
        documentName: asset.name || `Resume ${formatDate(new Date().toISOString())}`,
      });

      setUploadProgress(80);

      let newResume;
      if (uploadResult.success) {
        newResume = {
          id: uploadResult.data.document_id || uploadResult.data.id || `local_${Date.now()}`,
          document_name: uploadResult.data.document_name || asset.name,
          file_url: uploadResult.data.file_url || asset.uri,
          file_size: asset.size || 0,
          mime_type: asset.mimeType,
          created_at: uploadResult.data.created_at || new Date().toISOString(),
          is_active: true,
          local_uri: asset.uri,
        };
      } else {
        // Local-only fallback
        newResume = {
          id: `local_${Date.now()}`,
          document_name: asset.name,
          file_url: asset.uri,
          file_size: asset.size || 0,
          mime_type: asset.mimeType,
          created_at: new Date().toISOString(),
          is_active: true,
          local_only: true,
          local_uri: asset.uri,
        };
      }

      const updated = [newResume, ...resumes.map(r => ({ ...r, is_active: false }))];
      setResumes(updated);
      setActiveResumeId(newResume.id);
      await ProfileManagementService.setActiveResume(newResume.id);

      await analyzeResume(asset, newResume.id);

      Alert.alert(
        uploadResult.success ? 'Uploaded' : 'Saved locally',
        uploadResult.success
          ? 'Your resume is now on file.'
          : `Upload to server failed (${uploadResult.error || 'network'}). The file is saved on this device only.`
      );

      setUploadProgress(100);
      setTimeout(() => setIsUploading(false), 300);
    } catch (e) {
      setIsUploading(false);
      Alert.alert('Upload failed', e.message);
    }
  };

  const analyzeResume = async (asset, resumeId) => {
    setIsAnalyzing(true);
    try {
      // 1. Get profile data as the basis for analysis
      const profileResult = await ProfileManagementService.getProfile();
      const parsed = profileResult.success
        ? profileResult.data
        : { personalInfo: { name: 'Your Name' }, skills: [], experience: [], education: [] };

      setResumeData(parsed);

      // 2. Try the real ATS API first (server-side scoring)
      let score, feedback;
      const atsResult = await ProfileManagementService.runATSTest(parsed);
      if (atsResult.success) {
        score = atsResult.data.score;
        // Convert ATS recommendations to UI-friendly feedback format
        feedback = atsResult.data.recommendations.map((rec, i) => ({
          type: i < 2 ? 'warning' : 'tip',
          icon: 'bulb',
          message: rec,
        }));
        // Add missing-keywords as warnings
        (atsResult.data.keywords.missing || []).slice(0, 3).forEach(kw => {
          feedback.unshift({ type: 'warning', icon: 'key', message: `Consider adding "${kw}" — common in job postings for your field.` });
        });
      } else {
        // 3. Fall back to local scoring
        const local = ProfileManagementService.calculateLocalATSScore(parsed);
        score = local.score;
        feedback = ProfileManagementService.generateLocalFeedback(parsed);
      }

      setResumeScore(score);
      setAiFeedback(feedback);

      await ProfileManagementService.saveResumeAnalysis(resumeId, { data: parsed, score, feedback });
    } catch (e) {
      console.warn('analyzeResume:', e.message);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const setActiveResume = async (resumeId) => {
    const updated = resumes.map(r => ({ ...r, is_active: r.id === resumeId }));
    setResumes(updated);
    setActiveResumeId(resumeId);
    const active = updated.find(r => r.id === resumeId);
    if (active) await loadResumeAnalysis(active);
    await ProfileManagementService.setActiveResume(resumeId);
  };

  const deleteResume = (resumeId) => {
    Alert.alert(
      'Delete Resume?',
      'This will remove it from your library. This cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            const updated = resumes.filter(r => r.id !== resumeId);
            setResumes(updated);
            if (activeResumeId === resumeId) {
              setActiveResumeId(updated[0]?.id || null);
              if (updated[0]) await loadResumeAnalysis(updated[0]);
              else {
                setResumeData(null);
                setResumeScore(0);
                setAiFeedback([]);
              }
            }
            await ProfileManagementService.deleteResume(resumeId);
          },
        },
      ]
    );
  };

  // ════════════════════════════════════════════════════════════════════════════
  // Cover letter operations
  // ════════════════════════════════════════════════════════════════════════════
  const loadCoverLetters = async () => {
    if (!userId) return;
    try {
      const result = await ProfileManagementService.listCoverLetters();
      setCoverLetters(result.success ? (result.data || []) : []);
    } catch (e) {
      console.warn('loadCoverLetters:', e.message);
    }
  };

  const openCoverLetterModal = (existing = null) => {
    if (existing) {
      setClEditingId(existing.id);
      setClJobTitle(existing.job_title || '');
      setClCompany(existing.company || '');
      setClText(existing.text || existing.content || '');
      setClTemplate(existing.template || 'professional');
    } else {
      if (!resumeData) {
        Alert.alert('Upload a resume first', 'Cover letter generation pulls from your resume data.');
        return;
      }
      setClEditingId(null);
      setClJobTitle('');
      setClCompany('');
      setClText('');
      setClTemplate('professional');
    }
    setCoverLetterVisible(true);
  };

  const generateCoverLetter = () => {
    if (!clJobTitle.trim()) {
      Alert.alert('Job title required', 'Tell me what role this is for.');
      return;
    }
    if (!resumeData) {
      Alert.alert('No resume data', 'Upload a resume first to generate from.');
      return;
    }
    setGeneratingCL(true);
    try {
      const result = ProfileManagementService.generateCoverLetter(
        clTemplate,
        { jobTitle: clJobTitle, company: clCompany },
        resumeData
      );
      if (result.success) {
        setClText(result.data);
      } else {
        Alert.alert('Generation failed', result.error);
      }
    } finally {
      setGeneratingCL(false);
    }
  };

  const saveCoverLetter = async () => {
    if (!clText.trim()) {
      Alert.alert('Empty letter', 'Generate or write content first.');
      return;
    }
    setSavingCL(true);
    try {
      const name = `Cover Letter${clCompany ? ` - ${clCompany}` : ''}${clJobTitle ? ` (${clJobTitle})` : ''}`;
      const result = await ProfileManagementService.saveCoverLetter({
        id: clEditingId || undefined,
        job_title: clJobTitle.trim(),
        company: clCompany.trim(),
        template: clTemplate,
        text: clText,
        document_name: name,
      });

      if (result.success) {
        // Refresh from service to pick up the new record
        await loadCoverLetters();
        Alert.alert('Saved', 'Cover letter is in your library.');
        setCoverLetterVisible(false);
      } else {
        Alert.alert('Could not save', result.error || 'Try again');
      }
    } catch (e) {
      Alert.alert('Could not save', e.message);
    } finally {
      setSavingCL(false);
    }
  };

  const deleteCoverLetter = (id) => {
    Alert.alert(
      'Delete Cover Letter?',
      'This cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            await ProfileManagementService.deleteCoverLetter(id);
            await loadCoverLetters();
          },
        },
      ]
    );
  };

  // ════════════════════════════════════════════════════════════════════════════
  // Sharing
  // ════════════════════════════════════════════════════════════════════════════
  const openShare = (item) => {
    setShareItem(item);
    setShareVisible(true);
  };

  const shareViaNative = async () => {
    if (!shareItem) return;
    try {
      const message = shareItem.type === 'letter' ? shareItem.data.text : `My resume: ${shareItem.data.file_url || shareItem.data.document_name}`;
      const title = shareItem.data.document_name || (shareItem.type === 'letter' ? 'Cover Letter' : 'Resume');
      await Share.share({ message, title, url: shareItem.data.file_url });
    } catch {}
    setShareVisible(false);
  };

  const copyToClipboard = async () => {
    // RN doesn't have built-in clipboard; rely on Share API in fallback
    Alert.alert('Use Share', 'Tap "Share via apps" to send through your preferred app.');
  };

  // ════════════════════════════════════════════════════════════════════════════
  // Job tailoring
  // ════════════════════════════════════════════════════════════════════════════
  const runTailoring = () => {
    if (!resumeData) {
      Alert.alert('Upload a resume first', 'Tailoring needs your resume data to compare against the job.');
      return;
    }
    if (!jobDescription.trim() || jobDescription.length < 50) {
      Alert.alert('Need more detail', 'Paste the full job description (at least 50 characters).');
      return;
    }
    const result = ProfileManagementService.tailorToJob(jobDescription, resumeData);
    if (result.success) {
      setTailorResult(result.data);
    } else {
      Alert.alert('Tailoring failed', result.error);
    }
  };

  const generateTailoredCoverLetter = () => {
    if (!tailorResult || !resumeData) return;
    const jdLines = jobDescription.split('\n').filter(Boolean);
    const possibleTitle = jdLines.find(l => l.length < 80 && /[A-Z]/.test(l[0]))?.trim() || '';
    setClJobTitle(possibleTitle);
    setClCompany('');
    setClTemplate('modern');
    const result = ProfileManagementService.generateCoverLetter(
      'modern',
      { jobTitle: possibleTitle || 'this role', company: '' },
      resumeData
    );
    const baseLetter = result.success ? result.data : '';
    const keywordLine = tailorResult.matched_skills.length
      ? `\n\nKey overlap with your posting: ${tailorResult.matched_skills.join(', ')}.`
      : '';
    setClText(baseLetter + keywordLine);
    setTailorVisible(false);
    setCoverLetterVisible(true);
  };

  // ════════════════════════════════════════════════════════════════════════════
  // Pull-to-refresh
  // ════════════════════════════════════════════════════════════════════════════
  const onRefresh = async () => {
    setRefreshing(true);
    await Promise.all([loadResumes(), loadCoverLetters()]);
    setRefreshing(false);
  };

  // ════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════════════
  const activeResume = useMemo(() => resumes.find(r => r.id === activeResumeId), [resumes, activeResumeId]);

  // ── Header with tabs ────────────────────────────────────────────────────────
  const renderHeader = () => (
    <View style={s.header}>
      <View style={s.headerTopRow}>
        <TouchableOpacity onPress={() => navigation.goBack()} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
          <Ionicons name="chevron-back" size={26} color="#FFFFFF" />
        </TouchableOpacity>
        <Text style={s.headerTitle}>Resume & Cover Letters</Text>
        <View style={{ width: 26 }} />
      </View>

      <View style={s.tabBar}>
        {[
          { id: 'resumes', label: 'Resumes', icon: 'document-text' },
          { id: 'letters', label: 'Letters', icon: 'mail' },
          { id: 'tailor', label: 'Tailor', icon: 'flash' },
        ].map(t => (
          <TouchableOpacity
            key={t.id}
            style={[s.tab, tab === t.id && s.tabActive]}
            onPress={() => setTab(t.id)}
          >
            <Ionicons name={t.icon} size={18} color={tab === t.id ? COLORS.primary : '#FFFFFF'} />
            <Text style={[s.tabLabel, tab === t.id && s.tabLabelActive]}>{t.label}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );

  // ── Resumes tab ─────────────────────────────────────────────────────────────
  const renderResumesTab = () => (
    <ScrollView
      contentContainerStyle={{ padding: 16, paddingBottom: 60 }}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      showsVerticalScrollIndicator={false}
    >
      {/* Upload card */}
      <TouchableOpacity style={s.uploadCard} onPress={pickAndUploadResume} disabled={isUploading}>
        {isUploading ? (
          <View style={{ alignItems: 'center' }}>
            <ActivityIndicator size="large" color={COLORS.primary} />
            <Text style={s.uploadCardTitle}>Uploading… {uploadProgress}%</Text>
          </View>
        ) : (
          <>
            <View style={s.uploadIconWrap}>
              <Ionicons name="cloud-upload-outline" size={32} color={COLORS.primary} />
            </View>
            <Text style={s.uploadCardTitle}>Upload Resume</Text>
            <Text style={s.uploadCardSub}>PDF, Word, image — Max 10MB</Text>
          </>
        )}
      </TouchableOpacity>

      {/* Resume list */}
      {resumes.length === 0 ? (
        <View style={s.emptyState}>
          <MaterialCommunityIcons name="file-outline" size={48} color={COLORS.light} />
          <Text style={s.emptyTitle}>No resumes yet</Text>
          <Text style={s.emptySub}>Upload your first resume to get started</Text>
        </View>
      ) : (
        <>
          <Text style={s.sectionTitle}>Your Resumes ({resumes.length})</Text>
          {resumes.map(r => (
            <View key={r.id} style={[s.resumeCard, r.id === activeResumeId && s.resumeCardActive]}>
              <View style={s.resumeCardHeader}>
                <View style={[s.fileIconCircle, { backgroundColor: r.id === activeResumeId ? COLORS.primary : COLORS.bg }]}>
                  <MaterialCommunityIcons name={fileIconForMime(r.mime_type)} size={22} color={r.id === activeResumeId ? '#FFFFFF' : COLORS.primary} />
                </View>
                <View style={{ flex: 1, marginLeft: 12 }}>
                  <Text style={s.resumeName} numberOfLines={1}>{r.document_name || 'Resume'}</Text>
                  <Text style={s.resumeMeta}>
                    {formatBytes(r.file_size)} • {formatDate(r.created_at)}
                    {r.local_only ? ' • Local only' : ''}
                  </Text>
                </View>
                {r.id === activeResumeId && (
                  <View style={s.activeBadge}>
                    <Text style={s.activeBadgeText}>Active</Text>
                  </View>
                )}
              </View>

              <View style={s.resumeActions}>
                {r.id !== activeResumeId && (
                  <TouchableOpacity style={s.miniBtn} onPress={() => setActiveResume(r.id)}>
                    <Ionicons name="star-outline" size={16} color={COLORS.primary} />
                    <Text style={s.miniBtnText}>Set Active</Text>
                  </TouchableOpacity>
                )}
                <TouchableOpacity style={s.miniBtn} onPress={() => setPreviewVisible(true)}>
                  <Ionicons name="eye-outline" size={16} color={COLORS.primary} />
                  <Text style={s.miniBtnText}>Preview</Text>
                </TouchableOpacity>
                <TouchableOpacity style={s.miniBtn} onPress={() => openShare({ type: 'resume', data: r })}>
                  <Ionicons name="share-outline" size={16} color={COLORS.primary} />
                  <Text style={s.miniBtnText}>Share</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[s.miniBtn, s.miniBtnDanger]} onPress={() => deleteResume(r.id)}>
                  <Ionicons name="trash-outline" size={16} color={COLORS.danger} />
                </TouchableOpacity>
              </View>
            </View>
          ))}
        </>
      )}

      {/* Analysis section for active resume */}
      {activeResume && resumeData && (
        <>
          <Text style={s.sectionTitle}>Analysis</Text>
          <TouchableOpacity style={s.scoreCard} onPress={() => setScoreModalVisible(true)}>
            <View style={s.scoreLeft}>
              <Text style={s.scoreLabel}>ATS Score</Text>
              <Text style={[
                s.scoreValue,
                { color: resumeScore >= 70 ? COLORS.success : resumeScore >= 50 ? COLORS.warning : COLORS.danger },
              ]}>{resumeScore}<Text style={{ fontSize: 18, color: COLORS.sub }}>/100</Text></Text>
            </View>
            <View style={s.scoreBarWrap}>
              <View style={[
                s.scoreBar,
                {
                  width: `${resumeScore}%`,
                  backgroundColor: resumeScore >= 70 ? COLORS.success : resumeScore >= 50 ? COLORS.warning : COLORS.danger,
                },
              ]} />
            </View>
            <View style={s.scoreRight}>
              <Text style={s.scoreTapHint}>Tap for details</Text>
              <Ionicons name="chevron-forward" size={20} color={COLORS.sub} />
            </View>
          </TouchableOpacity>

          {/* Suggestions card */}
          {aiFeedback.length > 0 && (
            <TouchableOpacity style={s.tipsCard} onPress={() => setTipsVisible(true)}>
              <View style={s.tipsHeader}>
                <Ionicons name="bulb" size={22} color={COLORS.warning} />
                <Text style={s.tipsTitle}>{aiFeedback.length} Improvement{aiFeedback.length === 1 ? '' : 's'} Available</Text>
                <Ionicons name="chevron-forward" size={20} color={COLORS.sub} />
              </View>
              <Text style={s.tipsPreview} numberOfLines={2}>
                {aiFeedback[0]?.message || ''}
              </Text>
            </TouchableOpacity>
          )}

          {/* Quick actions */}
          <View style={s.quickActions}>
            <TouchableOpacity
              style={[s.quickActionBtn, { backgroundColor: COLORS.primary }]}
              onPress={() => navigation.navigate('BuildResume', { resumeData, mode: 'edit', source: 'upload' })}
            >
              <Ionicons name="create-outline" size={20} color="#FFFFFF" />
              <Text style={s.quickActionText}>Edit Resume</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[s.quickActionBtn, { backgroundColor: COLORS.success }]}
              onPress={() => setPreviewVisible(true)}
            >
              <Ionicons name="eye-outline" size={20} color="#FFFFFF" />
              <Text style={s.quickActionText}>Preview</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[s.quickActionBtn, { backgroundColor: COLORS.accent }]}
              onPress={() => openCoverLetterModal()}
            >
              <Ionicons name="document-text-outline" size={20} color="#FFFFFF" />
              <Text style={s.quickActionText}>Cover Letter</Text>
            </TouchableOpacity>
          </View>
        </>
      )}
    </ScrollView>
  );

  // ── Cover Letters tab ───────────────────────────────────────────────────────
  const renderLettersTab = () => (
    <ScrollView
      contentContainerStyle={{ padding: 16, paddingBottom: 60 }}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      showsVerticalScrollIndicator={false}
    >
      <TouchableOpacity style={s.newLetterCta} onPress={() => openCoverLetterModal()}>
        <View style={s.newLetterIconCircle}>
          <Ionicons name="add" size={28} color="#FFFFFF" />
        </View>
        <View style={{ flex: 1, marginLeft: 14 }}>
          <Text style={s.newLetterTitle}>New Cover Letter</Text>
          <Text style={s.newLetterSub}>Generate from your resume in seconds</Text>
        </View>
        <Ionicons name="chevron-forward" size={22} color={COLORS.primary} />
      </TouchableOpacity>

      {coverLetters.length === 0 ? (
        <View style={s.emptyState}>
          <MaterialCommunityIcons name="email-outline" size={48} color={COLORS.light} />
          <Text style={s.emptyTitle}>No cover letters yet</Text>
          <Text style={s.emptySub}>Create one tailored to each role you apply for</Text>
        </View>
      ) : (
        <>
          <Text style={s.sectionTitle}>Your Letters ({coverLetters.length})</Text>
          {coverLetters.map(cl => {
            const tmpl = COVER_LETTER_TEMPLATES.find(t => t.id === cl.template) || COVER_LETTER_TEMPLATES[0];
            return (
              <View key={cl.id} style={s.letterCard}>
                <View style={s.letterHeader}>
                  <View style={[s.letterTemplateChip, { backgroundColor: `${tmpl.color}20` }]}>
                    <Ionicons name={tmpl.icon} size={14} color={tmpl.color} />
                    <Text style={[s.letterTemplateText, { color: tmpl.color }]}>{tmpl.name}</Text>
                  </View>
                  <Text style={s.letterDate}>{formatDate(cl.created_at)}</Text>
                </View>
                <Text style={s.letterTitle} numberOfLines={1}>
                  {cl.job_title || 'Untitled'}{cl.company ? ` — ${cl.company}` : ''}
                </Text>
                <Text style={s.letterPreview} numberOfLines={3}>{cl.text || cl.content}</Text>
                <View style={s.letterActions}>
                  <TouchableOpacity style={s.miniBtn} onPress={() => openCoverLetterModal(cl)}>
                    <Ionicons name="create-outline" size={16} color={COLORS.primary} />
                    <Text style={s.miniBtnText}>Edit</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={s.miniBtn} onPress={() => openShare({ type: 'letter', data: cl })}>
                    <Ionicons name="share-outline" size={16} color={COLORS.primary} />
                    <Text style={s.miniBtnText}>Share</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={[s.miniBtn, s.miniBtnDanger]} onPress={() => deleteCoverLetter(cl.id)}>
                    <Ionicons name="trash-outline" size={16} color={COLORS.danger} />
                  </TouchableOpacity>
                </View>
              </View>
            );
          })}
        </>
      )}
    </ScrollView>
  );

  // ── Tailor tab ──────────────────────────────────────────────────────────────
  const renderTailorTab = () => (
    <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 60 }} showsVerticalScrollIndicator={false}>
      <View style={s.tailorIntro}>
        <Ionicons name="flash" size={24} color={COLORS.accent} />
        <Text style={s.tailorIntroTitle}>Tailor to a Job</Text>
        <Text style={s.tailorIntroSub}>
          Paste a job description below. We'll compare your resume, highlight matching skills, surface gaps, and give you a head start on a tailored cover letter.
        </Text>
      </View>

      <Text style={s.label}>Job Description</Text>
      <TextInput
        style={[s.input, s.tailorArea]}
        value={jobDescription}
        onChangeText={setJobDescription}
        placeholder="Paste the full job description here..."
        placeholderTextColor={COLORS.light}
        multiline
        textAlignVertical="top"
      />

      <TouchableOpacity
        style={[s.bigBtn, (!jobDescription.trim() || jobDescription.length < 50) && s.bigBtnDisabled]}
        disabled={!jobDescription.trim() || jobDescription.length < 50}
        onPress={runTailoring}
      >
        <Ionicons name="search" size={20} color="#FFFFFF" />
        <Text style={s.bigBtnText}>Analyze Match</Text>
      </TouchableOpacity>

      {tailorResult && (
        <View style={{ marginTop: 24 }}>
          <View style={s.matchCard}>
            <Text style={s.matchPercent}>{tailorResult.match_percentage}%</Text>
            <Text style={s.matchPercentLabel}>Skills Match</Text>
          </View>

          {tailorResult.matched_skills.length > 0 && (
            <View style={s.tagSection}>
              <Text style={s.tagSectionTitle}>
                <Ionicons name="checkmark-circle" size={16} color={COLORS.success} /> Matched skills ({tailorResult.matched_skills.length})
              </Text>
              <View style={s.tagWrap}>
                {tailorResult.matched_skills.map(k => (
                  <View key={k} style={[s.tag, { backgroundColor: `${COLORS.success}20`, borderColor: COLORS.success }]}>
                    <Text style={[s.tagText, { color: COLORS.success }]}>{k}</Text>
                  </View>
                ))}
              </View>
            </View>
          )}

          {tailorResult.suggested_keywords.length > 0 && (
            <View style={s.tagSection}>
              <Text style={s.tagSectionTitle}>
                <Ionicons name="add-circle" size={16} color={COLORS.warning} /> Consider adding ({tailorResult.suggested_keywords.length})
              </Text>
              <View style={s.tagWrap}>
                {tailorResult.suggested_keywords.map(k => (
                  <View key={k} style={[s.tag, { backgroundColor: `${COLORS.warning}20`, borderColor: COLORS.warning }]}>
                    <Text style={[s.tagText, { color: COLORS.warning }]}>{k}</Text>
                  </View>
                ))}
              </View>
            </View>
          )}

          {tailorResult.power_verbs.length > 0 && (
            <View style={s.tagSection}>
              <Text style={s.tagSectionTitle}>
                <Ionicons name="trending-up" size={16} color={COLORS.primary} /> Power verbs in JD
              </Text>
              <View style={s.tagWrap}>
                {tailorResult.power_verbs.map(k => (
                  <View key={k} style={[s.tag, { backgroundColor: `${COLORS.primary}20`, borderColor: COLORS.primary }]}>
                    <Text style={[s.tagText, { color: COLORS.primary }]}>{k}</Text>
                  </View>
                ))}
              </View>
            </View>
          )}

          <TouchableOpacity style={[s.bigBtn, { backgroundColor: COLORS.accent, marginTop: 16 }]} onPress={generateTailoredCoverLetter}>
            <Ionicons name="document-text" size={20} color="#FFFFFF" />
            <Text style={s.bigBtnText}>Generate Tailored Cover Letter</Text>
          </TouchableOpacity>
        </View>
      )}
    </ScrollView>
  );

  // ── Cover Letter Modal ──────────────────────────────────────────────────────
  const renderCoverLetterModal = () => (
    <Modal animationType="slide" transparent={false} visible={coverLetterVisible} onRequestClose={() => setCoverLetterVisible(false)}>
      <SafeAreaView style={s.modalSafe}>
        <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
          <View style={s.modalHeader}>
            <TouchableOpacity onPress={() => setCoverLetterVisible(false)} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
              <Ionicons name="close" size={26} color={COLORS.text} />
            </TouchableOpacity>
            <Text style={s.modalTitle}>{clEditingId ? 'Edit Cover Letter' : 'New Cover Letter'}</Text>
            <TouchableOpacity onPress={saveCoverLetter} disabled={savingCL || !clText.trim()} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
              {savingCL ? <ActivityIndicator color={COLORS.primary} /> : (
                <Text style={[s.modalSaveBtn, !clText.trim() && { color: COLORS.light }]}>Save</Text>
              )}
            </TouchableOpacity>
          </View>

          <ScrollView style={s.modalBody} keyboardShouldPersistTaps="handled">
            <Text style={s.label}>Template</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 10, paddingVertical: 6 }}>
              {COVER_LETTER_TEMPLATES.map(t => (
                <Pressable
                  key={t.id}
                  style={[
                    s.templateChip,
                    clTemplate === t.id && { borderColor: t.color, backgroundColor: `${t.color}15` },
                  ]}
                  onPress={() => {
                    setClTemplate(t.id);
                    if (clText && clJobTitle.trim()) {
                      const result = ProfileManagementService.generateCoverLetter(
                        t.id,
                        { jobTitle: clJobTitle, company: clCompany },
                        resumeData
                      );
                      if (result.success) setClText(result.data);
                    }
                  }}
                >
                  <Ionicons name={t.icon} size={18} color={clTemplate === t.id ? t.color : COLORS.sub} />
                  <Text style={[s.templateChipText, clTemplate === t.id && { color: t.color, fontWeight: '700' }]}>{t.name}</Text>
                </Pressable>
              ))}
            </ScrollView>
            <Text style={s.helperText}>
              {COVER_LETTER_TEMPLATES.find(t => t.id === clTemplate)?.description}
            </Text>

            <Text style={s.label}>Job Title <Text style={{ color: COLORS.danger }}>*</Text></Text>
            <TextInput
              style={s.input}
              value={clJobTitle}
              onChangeText={setClJobTitle}
              placeholder="e.g. Senior Software Engineer"
              placeholderTextColor={COLORS.light}
            />

            <Text style={s.label}>Company (Optional)</Text>
            <TextInput
              style={s.input}
              value={clCompany}
              onChangeText={setClCompany}
              placeholder="e.g. Acme Inc."
              placeholderTextColor={COLORS.light}
            />

            <TouchableOpacity
              style={[s.bigBtn, { backgroundColor: COLORS.accent, marginTop: 16 }, (!clJobTitle.trim() || generatingCL) && s.bigBtnDisabled]}
              onPress={generateCoverLetter}
              disabled={generatingCL || !clJobTitle.trim()}
            >
              {generatingCL ? <ActivityIndicator color="#FFFFFF" /> : (
                <>
                  <Ionicons name="sparkles" size={18} color="#FFFFFF" />
                  <Text style={s.bigBtnText}>{clText ? 'Regenerate' : 'Generate from Resume'}</Text>
                </>
              )}
            </TouchableOpacity>

            {clText ? (
              <>
                <View style={s.letterHeaderRow}>
                  <Text style={s.label}>Your Cover Letter</Text>
                  <TouchableOpacity onPress={() => Share.share({ message: clText, title: 'Cover Letter' })} style={s.inlineShareBtn}>
                    <Ionicons name="share-outline" size={16} color={COLORS.primary} />
                    <Text style={s.inlineShareBtnText}>Share</Text>
                  </TouchableOpacity>
                </View>
                <TextInput
                  style={[s.input, s.letterArea]}
                  value={clText}
                  onChangeText={setClText}
                  multiline
                  textAlignVertical="top"
                />
                <Text style={s.helperText}>You can edit any part of the draft.</Text>
              </>
            ) : null}
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
  );

  // ── Preview Modal ───────────────────────────────────────────────────────────
  const renderPreviewModal = () => (
    <Modal animationType="slide" transparent={false} visible={previewVisible} onRequestClose={() => setPreviewVisible(false)}>
      <SafeAreaView style={s.modalSafe}>
        <View style={s.modalHeader}>
          <TouchableOpacity onPress={() => setPreviewVisible(false)} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
            <Ionicons name="close" size={26} color={COLORS.text} />
          </TouchableOpacity>
          <Text style={s.modalTitle}>Resume Preview</Text>
          <TouchableOpacity onPress={() => openShare({ type: 'resume', data: activeResume })} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
            <Ionicons name="share-outline" size={22} color={COLORS.primary} />
          </TouchableOpacity>
        </View>
        <ScrollView style={s.modalBody}>
          {resumeData ? (
            <View style={s.previewWrap}>
              <Text style={s.previewName}>
                {resumeData.personalInfo?.name || resumeData.personalInfo?.fullName || resumeData.fullName || 'Your Name'}
              </Text>
              <Text style={s.previewContact}>
                {[resumeData.personalInfo?.email, resumeData.personalInfo?.phone, resumeData.personalInfo?.location]
                  .filter(Boolean).join(' • ')}
              </Text>

              {(resumeData.summary || resumeData.professionalSummary) && (
                <>
                  <Text style={s.previewSectionTitle}>Summary</Text>
                  <Text style={s.previewBody}>{resumeData.summary || resumeData.professionalSummary}</Text>
                </>
              )}

              {Array.isArray(resumeData.experience) && resumeData.experience.length > 0 && (
                <>
                  <Text style={s.previewSectionTitle}>Experience</Text>
                  {resumeData.experience.map((e, i) => (
                    <View key={i} style={s.previewItem}>
                      <Text style={s.previewItemTitle}>{e.title || e.position}</Text>
                      <Text style={s.previewItemSub}>{e.company} {e.startDate && `• ${e.startDate}`}{e.endDate && ` – ${e.endDate}`}</Text>
                      {e.description && <Text style={s.previewItemBody}>{e.description}</Text>}
                    </View>
                  ))}
                </>
              )}

              {Array.isArray(resumeData.skills) && resumeData.skills.length > 0 && (
                <>
                  <Text style={s.previewSectionTitle}>Skills</Text>
                  <View style={s.tagWrap}>
                    {resumeData.skills.map((sk, i) => (
                      <View key={i} style={s.previewTag}>
                        <Text style={s.previewTagText}>{typeof sk === 'string' ? sk : sk?.name || sk?.title}</Text>
                      </View>
                    ))}
                  </View>
                </>
              )}

              {Array.isArray(resumeData.education) && resumeData.education.length > 0 && (
                <>
                  <Text style={s.previewSectionTitle}>Education</Text>
                  {resumeData.education.map((ed, i) => (
                    <View key={i} style={s.previewItem}>
                      <Text style={s.previewItemTitle}>{ed.degree || ed.qualification}</Text>
                      <Text style={s.previewItemSub}>{ed.institution || ed.school} {ed.year && `• ${ed.year}`}</Text>
                    </View>
                  ))}
                </>
              )}
            </View>
          ) : (
            <View style={s.emptyState}>
              <Text style={s.emptyTitle}>No parsed data</Text>
              <Text style={s.emptySub}>Upload a resume and complete analysis first.</Text>
            </View>
          )}
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  // ── Score Modal ─────────────────────────────────────────────────────────────
  const renderScoreModal = () => {
    const breakdown = useMemo(() => {
      if (!resumeData) return [];
      const personalInfo = resumeData.personalInfo || resumeData;
      const experience = Array.isArray(resumeData.experience) ? resumeData.experience : [];
      const skills = Array.isArray(resumeData.skills) ? resumeData.skills : [];
      const education = Array.isArray(resumeData.education) ? resumeData.education : [];
      return [
        { label: 'Contact Info', score: (personalInfo.name ? 10 : 0) + (personalInfo.email ? 8 : 0) + (personalInfo.phone ? 7 : 0), max: 25 },
        { label: 'Summary', score: (resumeData.summary || resumeData.professionalSummary || personalInfo.summary) ? 15 : 0, max: 15 },
        { label: 'Skills', score: skills.length >= 5 ? 15 : skills.length > 0 ? 8 : 0, max: 15 },
        { label: 'Experience', score: experience.length >= 3 ? 20 : experience.length > 0 ? 12 : 0, max: 20 },
        { label: 'Education', score: education.length > 0 ? 10 : 0, max: 10 },
        { label: 'Projects / Certs', score: (resumeData.projects?.length ? 8 : 0) + (resumeData.certifications?.length ? 7 : 0), max: 15 },
      ];
    }, [resumeData]);

    return (
      <Modal animationType="slide" transparent={false} visible={scoreModalVisible} onRequestClose={() => setScoreModalVisible(false)}>
        <SafeAreaView style={s.modalSafe}>
          <View style={s.modalHeader}>
            <TouchableOpacity onPress={() => setScoreModalVisible(false)} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
              <Ionicons name="close" size={26} color={COLORS.text} />
            </TouchableOpacity>
            <Text style={s.modalTitle}>ATS Score Breakdown</Text>
            <View style={{ width: 26 }} />
          </View>
          <ScrollView style={s.modalBody}>
            <View style={s.bigScoreWrap}>
              <Text style={[
                s.bigScore,
                { color: resumeScore >= 70 ? COLORS.success : resumeScore >= 50 ? COLORS.warning : COLORS.danger },
              ]}>{resumeScore}<Text style={{ fontSize: 24, color: COLORS.sub }}>/100</Text></Text>
              <Text style={s.bigScoreLabel}>
                {resumeScore >= 80 ? 'Excellent — well-structured for ATS systems'
                 : resumeScore >= 60 ? 'Good — minor improvements would help'
                 : resumeScore >= 40 ? 'Fair — significant gaps to address'
                 : 'Needs work — see suggestions below'}
              </Text>
            </View>
            {breakdown.map((b, i) => (
              <View key={i} style={s.breakdownRow}>
                <View style={{ flex: 1 }}>
                  <Text style={s.breakdownLabel}>{b.label}</Text>
                  <View style={s.breakdownBarWrap}>
                    <View style={[s.breakdownBar, { width: `${(b.score / b.max) * 100}%` }]} />
                  </View>
                </View>
                <Text style={s.breakdownScore}>{b.score}/{b.max}</Text>
              </View>
            ))}
          </ScrollView>
        </SafeAreaView>
      </Modal>
    );
  };

  // ── Tips Modal ──────────────────────────────────────────────────────────────
  const renderTipsModal = () => (
    <Modal animationType="slide" transparent={false} visible={tipsVisible} onRequestClose={() => setTipsVisible(false)}>
      <SafeAreaView style={s.modalSafe}>
        <View style={s.modalHeader}>
          <TouchableOpacity onPress={() => setTipsVisible(false)} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
            <Ionicons name="close" size={26} color={COLORS.text} />
          </TouchableOpacity>
          <Text style={s.modalTitle}>Improvement Tips</Text>
          <View style={{ width: 26 }} />
        </View>
        <ScrollView style={s.modalBody}>
          {aiFeedback.map((f, i) => {
            const color = f.type === 'error' ? COLORS.danger : f.type === 'warning' ? COLORS.warning : COLORS.primary;
            const bgColor = f.type === 'error' ? `${COLORS.danger}15` : f.type === 'warning' ? `${COLORS.warning}15` : `${COLORS.primary}10`;
            return (
              <View key={i} style={[s.tipRow, { backgroundColor: bgColor, borderLeftColor: color }]}>
                <Ionicons name={f.icon || 'bulb'} size={20} color={color} />
                <Text style={[s.tipText, { color: COLORS.text }]}>{f.message}</Text>
              </View>
            );
          })}
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  // ── Share Modal ─────────────────────────────────────────────────────────────
  const renderShareModal = () => (
    <Modal animationType="fade" transparent visible={shareVisible} onRequestClose={() => setShareVisible(false)}>
      <Pressable style={s.shareBackdrop} onPress={() => setShareVisible(false)}>
        <Pressable style={s.shareSheet}>
          <View style={s.shareHandle} />
          <Text style={s.shareTitle}>Share {shareItem?.type === 'letter' ? 'Cover Letter' : 'Resume'}</Text>
          <View style={s.shareOptions}>
            <TouchableOpacity style={s.shareOption} onPress={shareViaNative}>
              <View style={[s.shareIconCircle, { backgroundColor: '#0e4e9620' }]}>
                <Ionicons name="share-social" size={26} color={COLORS.primary} />
              </View>
              <Text style={s.shareOptionLabel}>Share Sheet</Text>
            </TouchableOpacity>
            <TouchableOpacity style={s.shareOption} onPress={() => {
              setShareVisible(false);
              Share.share({ message: shareItem?.data?.text || shareItem?.data?.file_url || '', title: shareItem?.data?.document_name });
            }}>
              <View style={[s.shareIconCircle, { backgroundColor: '#10B98120' }]}>
                <Ionicons name="mail" size={26} color={COLORS.success} />
              </View>
              <Text style={s.shareOptionLabel}>Email</Text>
            </TouchableOpacity>
            <TouchableOpacity style={s.shareOption} onPress={() => {
              setShareVisible(false);
              Share.share({ message: shareItem?.data?.file_url || shareItem?.data?.text || '', title: 'Copy link' });
            }}>
              <View style={[s.shareIconCircle, { backgroundColor: '#F59E0B20' }]}>
                <Ionicons name="link" size={26} color={COLORS.warning} />
              </View>
              <Text style={s.shareOptionLabel}>Copy Link</Text>
            </TouchableOpacity>
            {shareItem?.type === 'letter' && (
              <TouchableOpacity style={s.shareOption} onPress={() => {
                setShareVisible(false);
                Share.share({ message: shareItem?.data?.text || '', title: 'Cover Letter' });
              }}>
                <View style={[s.shareIconCircle, { backgroundColor: '#8B5CF620' }]}>
                  <Ionicons name="document-text" size={26} color={COLORS.accent} />
                </View>
                <Text style={s.shareOptionLabel}>Text</Text>
              </TouchableOpacity>
            )}
          </View>
          <TouchableOpacity style={s.shareCancel} onPress={() => setShareVisible(false)}>
            <Text style={s.shareCancelText}>Cancel</Text>
          </TouchableOpacity>
        </Pressable>
      </Pressable>
    </Modal>
  );

  // ── Main return ─────────────────────────────────────────────────────────────
  return (
    <SafeAreaView style={s.safe}>
      <StatusBar barStyle="light-content" backgroundColor={COLORS.primary} />
      {renderHeader()}
      {tab === 'resumes' && renderResumesTab()}
      {tab === 'letters' && renderLettersTab()}
      {tab === 'tailor' && renderTailorTab()}
      {renderCoverLetterModal()}
      {renderPreviewModal()}
      {renderScoreModal()}
      {renderTipsModal()}
      {renderShareModal()}
    </SafeAreaView>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Styles
// ──────────────────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  safe: { flex: 1, backgroundColor: COLORS.primary },

  // ─ Header
  header: {
    backgroundColor: COLORS.primary,
    paddingBottom: 16,
    borderBottomLeftRadius: 22,
    borderBottomRightRadius: 22,
  },
  headerTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  headerTitle: { color: '#FFFFFF', fontSize: 17, fontWeight: '700' },
  tabBar: {
    flexDirection: 'row',
    backgroundColor: 'rgba(255,255,255,0.15)',
    marginHorizontal: 16,
    borderRadius: 12,
    padding: 4,
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    gap: 6,
    borderRadius: 10,
  },
  tabActive: { backgroundColor: '#FFFFFF' },
  tabLabel: { color: '#FFFFFF', fontSize: 13, fontWeight: '600' },
  tabLabelActive: { color: COLORS.primary, fontWeight: '700' },

  // ─ Body containers
  bodyContainer: { flex: 1, backgroundColor: COLORS.bg },

  // ─ Upload card
  uploadCard: {
    backgroundColor: COLORS.surface,
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: COLORS.border,
    borderStyle: 'dashed',
    marginBottom: 20,
  },
  uploadIconWrap: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: `${COLORS.primary}15`,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  uploadCardTitle: { fontSize: 16, fontWeight: '700', color: COLORS.text, marginBottom: 4 },
  uploadCardSub: { fontSize: 12, color: COLORS.sub },

  // ─ Section headers
  sectionTitle: { fontSize: 14, fontWeight: '700', color: COLORS.text, marginTop: 8, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 },

  // ─ Resume card
  resumeCard: {
    backgroundColor: COLORS.surface,
    borderRadius: 12,
    padding: 14,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  resumeCardActive: { borderColor: COLORS.primary, backgroundColor: `${COLORS.primary}08` },
  resumeCardHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  fileIconCircle: { width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center' },
  resumeName: { fontSize: 14, fontWeight: '700', color: COLORS.text },
  resumeMeta: { fontSize: 12, color: COLORS.sub, marginTop: 2 },
  activeBadge: { backgroundColor: COLORS.primary, paddingHorizontal: 10, paddingVertical: 4, borderRadius: 10 },
  activeBadgeText: { color: '#FFFFFF', fontSize: 11, fontWeight: '700' },
  resumeActions: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },

  miniBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    backgroundColor: `${COLORS.primary}10`,
    paddingHorizontal: 10, paddingVertical: 6,
    borderRadius: 8,
  },
  miniBtnDanger: { backgroundColor: `${COLORS.danger}10` },
  miniBtnText: { fontSize: 12, fontWeight: '600', color: COLORS.primary },

  // ─ Score card
  scoreCard: {
    backgroundColor: COLORS.surface,
    borderRadius: 12,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: COLORS.border,
    marginBottom: 12,
  },
  scoreLeft: { marginRight: 12 },
  scoreLabel: { fontSize: 11, color: COLORS.sub, fontWeight: '600', textTransform: 'uppercase' },
  scoreValue: { fontSize: 32, fontWeight: '800', marginTop: 2 },
  scoreBarWrap: { flex: 1, height: 8, backgroundColor: COLORS.bg, borderRadius: 4, overflow: 'hidden', marginHorizontal: 8 },
  scoreBar: { height: 8, borderRadius: 4 },
  scoreRight: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  scoreTapHint: { fontSize: 11, color: COLORS.sub },

  // ─ Tips card
  tipsCard: {
    backgroundColor: COLORS.surface,
    borderRadius: 12,
    padding: 14,
    borderWidth: 1,
    borderColor: COLORS.border,
    marginBottom: 16,
  },
  tipsHeader: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 6 },
  tipsTitle: { flex: 1, fontSize: 14, fontWeight: '700', color: COLORS.text },
  tipsPreview: { fontSize: 13, color: COLORS.sub, lineHeight: 18 },

  // ─ Quick actions row
  quickActions: { flexDirection: 'row', gap: 8, marginBottom: 16 },
  quickActionBtn: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 12, borderRadius: 10 },
  quickActionText: { color: '#FFFFFF', fontSize: 12, fontWeight: '700' },

  // ─ Empty state
  emptyState: { alignItems: 'center', padding: 32, marginVertical: 16 },
  emptyTitle: { fontSize: 16, fontWeight: '700', color: COLORS.text, marginTop: 8 },
  emptySub: { fontSize: 13, color: COLORS.sub, marginTop: 4, textAlign: 'center' },

  // ─ Letter list
  newLetterCta: {
    backgroundColor: COLORS.surface,
    borderRadius: 14,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  newLetterIconCircle: {
    width: 48, height: 48, borderRadius: 24,
    backgroundColor: COLORS.primary,
    alignItems: 'center', justifyContent: 'center',
  },
  newLetterTitle: { fontSize: 15, fontWeight: '700', color: COLORS.text },
  newLetterSub: { fontSize: 12, color: COLORS.sub, marginTop: 2 },

  letterCard: {
    backgroundColor: COLORS.surface,
    borderRadius: 12,
    padding: 14,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  letterHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 },
  letterTemplateChip: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10 },
  letterTemplateText: { fontSize: 11, fontWeight: '700' },
  letterDate: { fontSize: 11, color: COLORS.sub },
  letterTitle: { fontSize: 14, fontWeight: '700', color: COLORS.text },
  letterPreview: { fontSize: 12, color: COLORS.sub, marginTop: 4, lineHeight: 18, marginBottom: 10 },
  letterActions: { flexDirection: 'row', gap: 8 },

  // ─ Tailor tab
  tailorIntro: { padding: 20, alignItems: 'center', backgroundColor: COLORS.surface, borderRadius: 14, marginBottom: 16, borderWidth: 1, borderColor: COLORS.border },
  tailorIntroTitle: { fontSize: 18, fontWeight: '700', color: COLORS.text, marginTop: 8 },
  tailorIntroSub: { fontSize: 13, color: COLORS.sub, marginTop: 6, textAlign: 'center', lineHeight: 18 },

  // ─ Inputs
  label: { fontSize: 13, fontWeight: '700', color: COLORS.text, marginTop: 14, marginBottom: 6 },
  input: { backgroundColor: COLORS.surface, borderWidth: 1, borderColor: COLORS.border, borderRadius: 10, paddingHorizontal: 14, paddingVertical: 12, fontSize: 14, color: COLORS.text },
  tailorArea: { minHeight: 180, paddingTop: 14 },
  helperText: { fontSize: 12, color: COLORS.sub, marginTop: 6, fontStyle: 'italic' },

  // ─ Buttons
  bigBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 14, borderRadius: 12, backgroundColor: COLORS.primary, marginTop: 16 },
  bigBtnDisabled: { backgroundColor: COLORS.light },
  bigBtnText: { color: '#FFFFFF', fontSize: 15, fontWeight: '700' },

  // ─ Tailor match
  matchCard: { backgroundColor: COLORS.surface, borderRadius: 14, padding: 24, alignItems: 'center', borderWidth: 1, borderColor: COLORS.border, marginBottom: 16 },
  matchPercent: { fontSize: 48, fontWeight: '800', color: COLORS.primary },
  matchPercentLabel: { fontSize: 14, color: COLORS.sub, fontWeight: '600', textTransform: 'uppercase', marginTop: 4 },

  tagSection: { marginBottom: 18 },
  tagSectionTitle: { fontSize: 13, fontWeight: '700', color: COLORS.text, marginBottom: 8 },
  tagWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  tag: { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 14, borderWidth: 1 },
  tagText: { fontSize: 12, fontWeight: '600' },

  // ─ Modal
  modalSafe: { flex: 1, backgroundColor: COLORS.surface },
  modalHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: COLORS.border },
  modalTitle: { fontSize: 17, fontWeight: '700', color: COLORS.text },
  modalSaveBtn: { fontSize: 16, fontWeight: '700', color: COLORS.primary },
  modalBody: { flex: 1, paddingHorizontal: 20, paddingTop: 4 },

  templateChip: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingHorizontal: 14, paddingVertical: 8,
    borderRadius: 20, borderWidth: 1, borderColor: COLORS.border,
    backgroundColor: COLORS.surface,
  },
  templateChipText: { fontSize: 13, color: COLORS.sub, fontWeight: '600' },

  letterHeaderRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 14 },
  inlineShareBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 8, backgroundColor: `${COLORS.primary}10`, marginBottom: 6 },
  inlineShareBtnText: { fontSize: 12, fontWeight: '600', color: COLORS.primary },
  letterArea: { minHeight: 280, paddingTop: 14, fontSize: 14, lineHeight: 20 },

  // ─ Preview
  previewWrap: { paddingVertical: 16 },
  previewName: { fontSize: 22, fontWeight: '800', color: COLORS.text },
  previewContact: { fontSize: 13, color: COLORS.sub, marginTop: 4, marginBottom: 16 },
  previewSectionTitle: { fontSize: 13, fontWeight: '800', color: COLORS.primary, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 18, marginBottom: 8, paddingBottom: 4, borderBottomWidth: 1, borderBottomColor: COLORS.border },
  previewBody: { fontSize: 13, color: COLORS.text, lineHeight: 20 },
  previewItem: { marginBottom: 12 },
  previewItemTitle: { fontSize: 14, fontWeight: '700', color: COLORS.text },
  previewItemSub: { fontSize: 12, color: COLORS.sub, marginTop: 2 },
  previewItemBody: { fontSize: 13, color: COLORS.text, marginTop: 6, lineHeight: 18 },
  previewTag: { backgroundColor: `${COLORS.primary}15`, paddingHorizontal: 10, paddingVertical: 5, borderRadius: 14 },
  previewTagText: { fontSize: 12, color: COLORS.primary, fontWeight: '600' },

  // ─ Score modal
  bigScoreWrap: { alignItems: 'center', paddingVertical: 20, marginBottom: 20 },
  bigScore: { fontSize: 64, fontWeight: '800' },
  bigScoreLabel: { fontSize: 13, color: COLORS.sub, textAlign: 'center', marginTop: 8, paddingHorizontal: 24 },
  breakdownRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 16 },
  breakdownLabel: { fontSize: 13, fontWeight: '600', color: COLORS.text, marginBottom: 6 },
  breakdownBarWrap: { height: 8, backgroundColor: COLORS.bg, borderRadius: 4, overflow: 'hidden' },
  breakdownBar: { height: 8, backgroundColor: COLORS.primary, borderRadius: 4 },
  breakdownScore: { fontSize: 13, fontWeight: '700', color: COLORS.text, marginLeft: 12, width: 50, textAlign: 'right' },

  // ─ Tips modal
  tipRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, padding: 14, borderRadius: 10, marginBottom: 10, borderLeftWidth: 4 },
  tipText: { flex: 1, fontSize: 13, lineHeight: 18 },

  // ─ Share sheet
  shareBackdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  shareSheet: { backgroundColor: COLORS.surface, borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: 20, paddingBottom: 32 },
  shareHandle: { width: 40, height: 4, backgroundColor: COLORS.border, borderRadius: 2, alignSelf: 'center', marginBottom: 16 },
  shareTitle: { fontSize: 17, fontWeight: '700', color: COLORS.text, textAlign: 'center', marginBottom: 20 },
  shareOptions: { flexDirection: 'row', justifyContent: 'space-around', marginBottom: 16, flexWrap: 'wrap', gap: 16 },
  shareOption: { alignItems: 'center', minWidth: 70 },
  shareIconCircle: { width: 56, height: 56, borderRadius: 28, alignItems: 'center', justifyContent: 'center', marginBottom: 8 },
  shareOptionLabel: { fontSize: 12, color: COLORS.text, fontWeight: '600' },
  shareCancel: { paddingVertical: 14, borderRadius: 12, backgroundColor: COLORS.bg, alignItems: 'center' },
  shareCancelText: { fontSize: 15, fontWeight: '700', color: COLORS.text },
});
